/**
 * Rax Schedule List
 *
 * iOS alarm-style list for viewing and managing scheduled automations.
 * Time is the hero - prominently displayed with toggle switches.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi, type AutomationRule, type AutomationRuleStatus } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

interface ParsedSchedule {
  hour: number;
  minute: number;
  days: number[]; // 0=Sun, 1=Mon, etc.
  timezone: string;
}

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

@customElement("rax-schedule-list")
export class RaxScheduleList extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .schedule-container {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }
      
      .schedule-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
        background: var(--bg-elevated);
      }
      
      .schedule-header h2 {
        margin: 0;
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .add-btn {
        background: var(--rax-yellow);
        border: none;
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-3);
        font-size: var(--font-sm);
        font-weight: 600;
        color: #000;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .add-btn:hover {
        background: var(--rax-yellow-hover);
        transform: scale(1.02);
      }
      
      .schedule-list {
        display: flex;
        flex-direction: column;
      }
      
      .schedule-item {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-4) var(--space-5);
        border-bottom: 1px solid var(--border-color);
        transition: background var(--transition-fast);
        cursor: pointer;
      }
      
      .schedule-item:last-child {
        border-bottom: none;
      }
      
      .schedule-item:hover {
        background: var(--bg-hover);
      }
      
      .schedule-item.disabled {
        opacity: 0.5;
      }
      
      .time-display {
        min-width: 100px;
      }
      
      .time-main {
        font-size: 2.25rem;
        font-weight: 300;
        color: var(--text-primary);
        letter-spacing: -0.02em;
        line-height: 1;
        font-variant-numeric: tabular-nums;
      }
      
      .time-period {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
        font-weight: 500;
        margin-left: var(--space-1);
      }
      
      .schedule-info {
        flex: 1;
        min-width: 0;
      }
      
      .schedule-name {
        font-size: var(--font-md);
        font-weight: 500;
        color: var(--text-primary);
        margin: 0 0 var(--space-1);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .schedule-meta {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        font-size: var(--font-sm);
        color: var(--text-tertiary);
      }
      
      .day-pills {
        display: flex;
        gap: 2px;
      }
      
      .day-pill {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 600;
        background: var(--bg-subtle);
        color: var(--text-tertiary);
      }
      
      .day-pill.active {
        background: var(--rax-yellow);
        color: #000;
      }
      
      .next-run {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .next-run.soon {
        color: var(--rax-yellow);
      }
      
      /* iOS-style toggle switch */
      .toggle-switch {
        position: relative;
        width: 51px;
        height: 31px;
        flex-shrink: 0;
      }
      
      .toggle-switch input {
        opacity: 0;
        width: 0;
        height: 0;
      }
      
      .toggle-slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--bg-subtle);
        border-radius: 31px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .toggle-slider:before {
        position: absolute;
        content: "";
        height: 27px;
        width: 27px;
        left: 2px;
        bottom: 2px;
        background-color: white;
        border-radius: 50%;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
      
      .toggle-switch input:checked + .toggle-slider {
        background-color: #34c759;
      }
      
      .toggle-switch input:checked + .toggle-slider:before {
        transform: translateX(20px);
      }
      
      .toggle-switch input:disabled + .toggle-slider {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .empty-state {
        padding: var(--space-8);
        text-align: center;
        color: var(--text-tertiary);
      }
      
      .empty-state-icon {
        font-size: 3rem;
        margin-bottom: var(--space-4);
        opacity: 0.5;
      }
      
      .empty-state h3 {
        margin: 0 0 var(--space-2);
        font-size: var(--font-lg);
        color: var(--text-secondary);
      }
      
      .empty-state p {
        margin: 0;
        font-size: var(--font-sm);
      }
      
      .loading-state {
        padding: var(--space-8);
        text-align: center;
        color: var(--text-tertiary);
      }
      
      .error-state {
        padding: var(--space-4);
        background: rgba(239, 68, 68, 0.14);
        border: 1px solid rgba(239, 68, 68, 0.55);
        border-radius: var(--radius-md);
        color: #fca5a5;
        margin: var(--space-4);
      }
      
      /* Swipe-to-delete indicator */
      .delete-hint {
        position: absolute;
        right: var(--space-4);
        opacity: 0;
        color: var(--error);
        font-size: var(--font-sm);
        transition: opacity var(--transition-fast);
      }
      
      .schedule-item:active .delete-hint {
        opacity: 0.5;
      }
      
      @media (max-width: 480px) {
        .time-main {
          font-size: 1.75rem;
        }
      
        .schedule-item {
          padding: var(--space-3) var(--space-4);
        }
      
        .day-pills {
          display: none;
        }
      }
    `,
  ];

  @property({ type: String })
  agentId: string = "";

  @state()
  private schedules: AutomationRule[] = [];

  @state()
  private loading = true;

  @state()
  private error: string | null = null;

  @state()
  private actionInProgress: Record<string, boolean> = {};

  override connectedCallback() {
    super.connectedCallback();
    this.loadSchedules();
  }

  private async loadSchedules() {
    this.loading = true;
    this.error = null;

    try {
      const response = await raxApi.listAutomationRules();
      if (response.success && response.data) {
        // Filter to only schedule-type rules
        this.schedules = response.data.filter((r) => r.triggerType === "schedule");
      } else {
        this.error = response.error || "Failed to load schedules";
      }
    } catch (err) {
      console.error("[ScheduleList] Error loading schedules:", err);
      this.error = err instanceof Error ? err.message : "Failed to load schedules";
    } finally {
      this.loading = false;
    }
  }

  private parseSchedule(rule: AutomationRule): ParsedSchedule | null {
    try {
      const config = rule.triggerConfig as Record<string, unknown>;
      const scheduleStr = config.schedule as string;

      if (!scheduleStr) return null;

      // Parse common schedule formats
      // Expected format: "HH:MM" or "HH:MM days" or cron-like
      const timeMatch = scheduleStr.match(/(\d{1,2}):(\d{2})/);
      if (!timeMatch) return null;

      const hour = parseInt(timeMatch[1], 10);
      const minute = parseInt(timeMatch[2], 10);

      // Parse days - look for day names or "weekday"/"daily"
      let days: number[] = [0, 1, 2, 3, 4, 5, 6]; // default: every day
      const lowerStr = scheduleStr.toLowerCase();

      if (lowerStr.includes("weekday") || lowerStr.includes("week day")) {
        days = [1, 2, 3, 4, 5];
      } else if (lowerStr.includes("weekend")) {
        days = [0, 6];
      } else {
        // Check for specific day names
        const foundDays: number[] = [];
        DAY_NAMES.forEach((name, idx) => {
          if (lowerStr.includes(name.toLowerCase().slice(0, 3))) {
            foundDays.push(idx);
          }
        });
        if (foundDays.length > 0) {
          days = foundDays;
        }
      }

      return {
        hour,
        minute,
        days,
        timezone: (config.timezone as string) || "local",
      };
    } catch {
      return null;
    }
  }

  private formatTime(hour: number, minute: number): { time: string; period: string } {
    const h = hour % 12 || 12;
    const m = minute.toString().padStart(2, "0");
    const period = hour < 12 ? "AM" : "PM";
    return { time: `${h}:${m}`, period };
  }

  private getNextRun(schedule: ParsedSchedule): string {
    const now = new Date();
    const today = now.getDay();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const scheduleMinutes = schedule.hour * 60 + schedule.minute;

    // Find next occurrence
    for (let offset = 0; offset <= 7; offset++) {
      const checkDay = (today + offset) % 7;
      if (schedule.days.includes(checkDay)) {
        if (offset === 0 && scheduleMinutes > currentMinutes) {
          const diff = scheduleMinutes - currentMinutes;
          if (diff < 60) return `in ${diff} min`;
          return `Today ${this.formatTime(schedule.hour, schedule.minute).time} ${this.formatTime(schedule.hour, schedule.minute).period}`;
        } else if (offset === 1) {
          return `Tomorrow ${this.formatTime(schedule.hour, schedule.minute).time} ${this.formatTime(schedule.hour, schedule.minute).period}`;
        } else if (offset > 0) {
          return `${DAY_NAMES[checkDay]} ${this.formatTime(schedule.hour, schedule.minute).time} ${this.formatTime(schedule.hour, schedule.minute).period}`;
        }
      }
    }
    return "No upcoming run";
  }

  private isNextRunSoon(schedule: ParsedSchedule): boolean {
    const now = new Date();
    const today = now.getDay();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const scheduleMinutes = schedule.hour * 60 + schedule.minute;

    if (schedule.days.includes(today) && scheduleMinutes > currentMinutes) {
      return scheduleMinutes - currentMinutes < 60;
    }
    return false;
  }

  override render() {
    return html`
      <div class="schedule-container">
        <div class="schedule-header">
          <h2>Schedules</h2>
          <button class="add-btn" @click=${this.handleAddClick}>+ Add</button>
        </div>

        ${
          this.loading
            ? html`
                <div class="loading-state">Loading schedules...</div>
              `
            : this.error
              ? html` <div class="error-state">${this.error}</div> `
              : this.schedules.length === 0
                ? html`
                    <div class="empty-state">
                      <div class="empty-state-icon">&#128337;</div>
                      <h3>No schedules yet</h3>
                      <p>Tell your agent what to do and when, like "Check my GitHub every morning at 9am"</p>
                    </div>
                  `
                : html`
                  <div class="schedule-list">
                    ${this.schedules.map((schedule) => this.renderScheduleItem(schedule))}
                  </div>
                `
        }
      </div>
    `;
  }

  private renderScheduleItem(rule: AutomationRule) {
    const parsed = this.parseSchedule(rule);
    const isActive = rule.status === "active";
    const inProgress = this.actionInProgress[rule.id] ?? false;

    if (!parsed) {
      // Fallback for unparseable schedules
      return html`
        <div class="schedule-item ${!isActive ? "disabled" : ""}" @click=${() => this.handleItemClick(rule)}>
          <div class="time-display">
            <span class="time-main">--:--</span>
          </div>
          <div class="schedule-info">
            <h3 class="schedule-name">${rule.name}</h3>
            <div class="schedule-meta">${rule.action}</div>
          </div>
          <label class="toggle-switch" @click=${(e: Event) => e.stopPropagation()}>
            <input
              type="checkbox"
              ?checked=${isActive}
              ?disabled=${inProgress}
              @change=${(e: Event) => this.handleToggle(e, rule.id)}
            />
            <span class="toggle-slider"></span>
          </label>
        </div>
      `;
    }

    const { time, period } = this.formatTime(parsed.hour, parsed.minute);
    const nextRun = this.getNextRun(parsed);
    const isSoon = this.isNextRunSoon(parsed);

    return html`
      <div class="schedule-item ${!isActive ? "disabled" : ""}" @click=${() => this.handleItemClick(rule)}>
        <div class="time-display">
          <span class="time-main">${time}</span>
          <span class="time-period">${period}</span>
        </div>
        <div class="schedule-info">
          <h3 class="schedule-name">${rule.name}</h3>
          <div class="schedule-meta">
            <div class="day-pills">
              ${DAY_LABELS.map(
                (label, idx) => html`
                  <span class="day-pill ${parsed.days.includes(idx) ? "active" : ""}">${label}</span>
                `,
              )}
            </div>
            ${isActive ? html` <span class="next-run ${isSoon ? "soon" : ""}">${nextRun}</span> ` : nothing}
          </div>
        </div>
        <label class="toggle-switch" @click=${(e: Event) => e.stopPropagation()}>
          <input
            type="checkbox"
            ?checked=${isActive}
            ?disabled=${inProgress}
            @change=${(e: Event) => this.handleToggle(e, rule.id)}
          />
          <span class="toggle-slider"></span>
        </label>
      </div>
    `;
  }

  private handleAddClick() {
    this.dispatchEvent(
      new CustomEvent("add-schedule", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleItemClick(rule: AutomationRule) {
    this.dispatchEvent(
      new CustomEvent("edit-schedule", {
        detail: { rule },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async handleToggle(e: Event, ruleId: string) {
    e.preventDefault();
    const input = e.target as HTMLInputElement;
    const newStatus: AutomationRuleStatus = input.checked ? "active" : "paused";

    this.actionInProgress = { ...this.actionInProgress, [ruleId]: true };

    try {
      const response = await raxApi.updateAutomationRuleStatus(ruleId, newStatus);
      if (response.success && response.data) {
        this.schedules = this.schedules.map((s) => (s.id === ruleId ? response.data! : s));
        this.dispatchEvent(
          new CustomEvent("schedule-toggled", {
            detail: { rule: response.data },
            bubbles: true,
            composed: true,
          }),
        );
      } else {
        // Revert the toggle
        input.checked = !input.checked;
        console.error("[ScheduleList] Failed to toggle:", response.error);
      }
    } catch (err) {
      input.checked = !input.checked;
      console.error("[ScheduleList] Error toggling:", err);
    } finally {
      this.actionInProgress = { ...this.actionInProgress, [ruleId]: false };
    }
  }

  /** Public method to refresh the schedules */
  public async refresh() {
    await this.loadSchedules();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-schedule-list": RaxScheduleList;
  }
}
